import { Link } from "@/lib/nav";
import type { CSSProperties, ReactNode } from "react";

import Money from "@/components/money";
import SectionCard from "@buildpy/ui/section-card";
import { EMPTY_MARK } from "@buildpy/core/lib/format";
import { cn } from "@/lib/utils";
import { changeOrderPath } from "@buildpy/core/modules/change-order/view";
import {
  type ChangeOrderStrip,
  type PaymentStrip,
  paymentStripHint,
  type ProgressStrip as ProgressStripData,
  stripDDayText,
} from "@buildpy/core/modules/project/phase-chart";

interface CellProps {
  title: string;
  value: ReactNode;
  hint: ReactNode;
  children?: ReactNode;
  testId: string;
}

const Cell = ({ title, value, hint, children, testId }: CellProps) => (
  <SectionCard as="div" className="flex min-w-0 flex-col gap-y-1.5 px-4 py-3" data-testid={testId} padding="none">
    <span className="text-sm text-muted-foreground">{title}</span>
    <span className="text-2xl font-semibold tabular-nums">{value}</span>
    {children}
    <span className="text-xs text-muted-foreground">{hint}</span>
  </SectionCard>
);

const Bar = ({ ratio, marker, className }: { ratio: number; marker?: boolean; className: string }) => (
  <div aria-hidden className="relative h-1.5 rounded-full bg-muted">
    <div className={cn("h-1.5 w-(--w) rounded-full", className)} style={{ "--w": `${Math.round(ratio * 100)}%` } as CSSProperties} />
    {marker && (
      <div
        className="absolute -top-1 left-(--x) h-3.5 w-0.5 bg-foreground"
        style={{ "--x": `calc(${Math.round(ratio * 100)}% - 1px)` } as CSSProperties}
      />
    )}
  </div>
);

interface Props {
  strip: ProgressStripData;
  // 지연을 세는 현장인지(시공중) — 아니면 「시공중일 때만」 으로 안내한다.
  countsDelay: boolean;
  // 대금·변경공사 칸 — 연결된 계약이 없으면(null) 칸은 두고 값만 비운다, 안 넘기면 칸이 없다.
  payments?: PaymentStrip | null;
  changeOrders?: ChangeOrderStrip | null;
}

// 현장 상세 머리의 진행 요약 — 공정률 · 공사 기간(오늘 위치·D-day) · 지연 · 대금 · 변경공사.
const ProgressStrip = ({ strip, countsDelay, payments, changeOrders }: Props) => {
  const { span } = strip;
  const dDay = stripDDayText(strip.dDay);
  const unscheduled = strip.unscheduled > 0 ? `일정 미정 ${strip.unscheduled}개` : "";
  return (
    <section
      aria-label="진행 요약"
      className={cn(
        "grid gap-3 sm:grid-cols-3",
        (payments !== undefined || changeOrders !== undefined) && "lg:grid-cols-5"
      )}
      data-testid="project-progress-strip">
      <Cell
        hint={
          strip.phaseTotal === 0
            ? "공정 없음"
            : `${strip.phaseTotal}개 공정 중 ${strip.phaseDone}개 완료 · ${strip.phaseActive}개 진행${strip.phaseHold > 0 ? ` · ${strip.phaseHold}개 보류` : ""}`
        }
        testId="strip-phase-rate"
        title="공정률"
        value={strip.phaseTotal === 0 ? EMPTY_MARK : `${strip.percent}%`}>
        <Bar className="bg-primary" ratio={strip.phaseTotal === 0 ? 0 : strip.phaseDone / strip.phaseTotal} />
      </Cell>
      <Cell
        hint={
          span
            ? [`${span.start.slice(5)} 착공 → ${span.end.slice(5)} 준공 예정`, dDay].filter(Boolean).join(" · ")
            : dDay || "착공일이 정해지면 기간이 보입니다"
        }
        testId="strip-span"
        title="공사 기간"
        value={span ? `${span.elapsed} / ${span.total}일` : EMPTY_MARK}>
        {span && <Bar className="bg-muted-foreground" marker ratio={span.ratio} />}
      </Cell>
      <Cell
        hint={[countsDelay ? "지금 늦어지는 공정" : "시공중일 때만 셉니다", unscheduled].filter(Boolean).join(" · ")}
        testId="strip-delay"
        title="지연"
        value={
          strip.delayed > 0 ? <span className="text-destructive">{strip.delayed}개</span> : countsDelay ? "없음" : EMPTY_MARK
        }
      />
      {payments !== undefined && (
        <Cell
          hint={payments ? paymentStripHint(payments) : "연결된 계약 없음"}
          testId="strip-payments"
          title="대금"
          value={payments && payments.total > 0 ? `${payments.paid} / ${payments.total}회` : EMPTY_MARK}>
          {payments && payments.total > 0 && (
            <div className="flex gap-1">
              <span className="sr-only">{`${payments.total}회 중 ${payments.paid}회 받음`}</span>
              {payments.dots.map((paid, i) => (
                <span
                  key={i}
                  aria-hidden
                  className={cn("h-1.5 flex-1 rounded-full", paid ? "bg-primary" : "bg-muted")}
                  data-paid={paid ? "true" : "false"}
                />
              ))}
            </div>
          )}
        </Cell>
      )}
      {changeOrders !== undefined && (
        <Cell
          hint={
            changeOrders?.latest ? (
              <>
                {changeOrders.latest.title} ·{" "}
                <Link
                  className="font-medium text-primary hover:underline"
                  data-testid="strip-change-order-latest"
                  href={changeOrderPath(changeOrders.latest.contract_id, changeOrders.latest.id)}>
                  CO-{changeOrders.latest.seq} 보기
                </Link>
              </>
            ) : changeOrders ? (
              "변경공사 없음"
            ) : (
              "연결된 계약 없음"
            )
          }
          testId="strip-change-orders"
          title="변경공사"
          value={changeOrders && changeOrders.latest ? <Money signed value={changeOrders.acceptedDelta} /> : EMPTY_MARK}>
          {changeOrders && changeOrders.waiting > 0 && (
            <span className="text-xs font-medium text-warning">{changeOrders.waiting}건 고객 답변 대기</span>
          )}
        </Cell>
      )}
    </section>
  );
};

export default ProgressStrip;
